import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '@services/core-services/api.service';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  AnalyticsResponse,
  DynamicQueryService,
  mapAnalyticsResponse,
  toISOString,
} from '@services/analytics-services/core';
import { PaymentAnalyticsQuery, PaymentStats } from '@services/analytics-services/payment/models';
import { CachingApiService } from '@services/core-services/api-cache.service';
import { ProtectionService } from '@services/core-services/protection.service';

@Injectable({ providedIn: 'root' })
export class PaymentAnalyticsService {
  constructor(
    private readonly api: ApiService,
    private readonly cacheApi: CachingApiService,
    private readonly protectionService: ProtectionService,
    private readonly dynamicQueryService: DynamicQueryService
  ) {}

  getAnalytics(context: PaymentAnalyticsQuery): Observable<AnalyticsInfo<PaymentStats>> {
    const { range, interval, compare, ...params } = context;

    return this.cacheApi
      .getWithCache<AnalyticsResponse<PaymentStats>>('orders/payments/metrics/stats', {
        ...params,
        date_from: toISOString(compare?.start || range.start),
        date_to: toISOString(range.end),
        interval,
      })
      .pipe(
        map(response => {
          return mapAnalyticsResponse<PaymentStats>(response, context);
        })
      );
  }

  getPaymentMethodDistribution(range: AnalyticsRangeContext, timezone?: string) {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id;
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const sql = `
        select
            CASE
                payment_method
                WHEN 'NB' THEN 'Net Banking'
                ELSE payment_method
            END as payment_method,
            uniqExact(order_id) as payment_count,
            sum(amount_to_pay) as amount
        from
            ce_replica.\`ce_sales.sales_order\` so
            JOIN ce_replica.\`ce_sales.sales_order_payment\` sop ON so.id = sop.order_id
        where
            store_id = '${storeId}'
            AND so.payment_status = 'success'
            AND toTimeZone(so.created_at, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        group by
            1
        order by payment_count desc
    `;

    return this.dynamicQueryService.fetchRaw(range, {
      raw_query: sql,
    });
  }
}
